import './TaxonomyEditor.scss'
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useStores} from "../../../stores";
import type {TaxonomyEditorOptions} from "./TaxonomyEditorOptions";
import {DEFAULT_TAXONOMY_EDITOR_OPTIONS} from "./TaxonomyEditorOptions";
import {TaxonomyTreeEditorController} from "./taxonomy-tree/TaxonomyTreeEditorController";
import {observer} from "mobx-react-lite";

type Props = {
    /**
     * Initial input data
     */
    options?: Partial<TaxonomyEditorOptions>,
};
/**
 * Simple component to hook up a controller which will manage the control flow between d3.js, React and Mobx
 */
export const TaxonomyEditor: React.FC<Props> = observer(({options}) => {
    const {taxonomyEditorStore, taxonomyManagerStore, authStore} = useStores()
    const wrapperRef = useRef<HTMLDivElement>(null)

    const [controller, setController] = useState<TaxonomyTreeEditorController | undefined>(undefined)
    const o = useMemo<TaxonomyEditorOptions>(() => ({
        ...DEFAULT_TAXONOMY_EDITOR_OPTIONS,
        ...options,
    }), [options])
    const taxonomy = taxonomyManagerStore.taxonomy;

    useEffect(() => {
        if (!wrapperRef.current) {
            // Cannot initialize controller without a wrapper element
            console.error('TaxonomyEditor.useEffect[]: wrapperRef.current is null');
            return;
        }

        const controller = new TaxonomyTreeEditorController(wrapperRef.current, o, taxonomyEditorStore, taxonomyManagerStore, authStore);
        setController(controller);

        return () => {
            controller?.destroy();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        // Initialize the taxonomy editor once, with the initial value of the taxonomy.
        // The taxonomy object will change over time, but the Controller will update itself.

        if (!taxonomy || !controller) {
            // If there is no taxonomy, do not set up the controller
            return
        }
        if (!controller.initialized) controller.initializeData(taxonomy)
    }, [taxonomy, controller])

    return <div ref={wrapperRef}/>
})
